import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@environment/environment';
import { AuthService } from '@app/auth/auth.service';

/**
 * Marca un request como "de fondo": no cuenta para el loading global (`LoadingService.loading$`),
 * que oculta botones como Aplicar / Descargar reporte mientras haya peticiones pendientes.
 * Uso: `http.get(url, { context: new HttpContext().set(SKIP_GLOBAL_LOADING, true) })`.
 */
export const SKIP_GLOBAL_LOADING = new HttpContextToken<boolean>(() => false);

/** Requests de fondo conocidos (sesión, menú, notificaciones, rangos del slider) que no deben bloquear la UI. */
const BACKGROUND_URL_PATTERNS: RegExp[] = [
  /\/usuario(\?|$)/,
  /\/user\/[^/?]+\/modules(\?|$)/,
  /\/v1\/notification(\/|\?|$)/,
  /\/v1\/Billing\/DateRanges(\?|$)/,
];

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private activeRequests = 0;

  constructor(
    private loadingService: LoadingService,
    private router: Router,
    private snackBar: MatSnackBar,
    private injector: Injector,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const tracked = !this.isBackgroundRequest(req);

    if (tracked) {
      if (this.activeRequests === 0) this.loadingService.showHttp();
      this.activeRequests++;
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Lazy: AuthService depende de HttpClient, inyectarlo en el constructor genera ciclo DI
          const authService = this.injector.get(AuthService);
          // Sin sesión previa (páginas públicas /legals) no hay nada que expirar: no redirigir
          const hadSession = !!authService.userValue;
          authService.clearSession();
          if (hadSession) {
            this.router.navigate(['']);
            if (this.router.url !== '/') {
              this.snackBar.open('Sesión expirada. Por favor, inicia sesión nuevamente.', 'Cerrar', {
                duration: 15000,
              });
            }
          }
        }
        return throwError(error);
      }),
      finalize(() => {
        if (!tracked) return;
        this.activeRequests--;
        if (this.activeRequests === 0) this.loadingService.hideHttp();
      })
    );
  }

  private isBackgroundRequest(req: HttpRequest<unknown>): boolean {
    if (req.context.get(SKIP_GLOBAL_LOADING)) return true;

    // API REST del hub de notificaciones (NotificationHubService.API_URL)
    const notificationsApi = environment.API_URL_NOTIFICATIONS ? `${environment.API_URL_NOTIFICATIONS}notifications` : '';
    if (notificationsApi && req.url.startsWith(notificationsApi)) return true;

    return BACKGROUND_URL_PATTERNS.some((pattern) => pattern.test(req.url));
  }
}
